// src/pages/team/TeamDelegationDetailPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import {
  delegationStatusMap,
  delegationReasonMap,
  getStatusMapping,
  canCancelDelegation,
  getTeamName,
  formatDateTime,
  refEntity,
} from '@/features/team/logic';

export const TeamDelegationDetailPage = observer(() => {
  const { id, delegationId } = useParams<{ id: string; delegationId: string }>();
  const store = useTeam();
  const { t, locale } = useI18n();

  const [delegation, setDelegation] = useState<any>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ reason_details: '', start_at: '', end_at: '' });
  const [submitting, setSubmitting] = useState(false);

  const loadDelegation = useCallback(async () => {
    if (!id || !delegationId) return;
    try {
      const res = await store.GetRoleDelegation(id, delegationId);
      const del: any = (res as any)?.delegation ?? res;
      setDelegation(del);
      setEditForm({
        reason_details: del?.reason_details || '',
        start_at: del?.start_at ? String(del.start_at).slice(0, 16) : '',
        end_at: del?.end_at ? String(del.end_at).slice(0, 16) : '',
      });
    } catch { /* store handles */ }
  }, [id, delegationId, store]);

  useEffect(() => {
    if (id) store.GetTeam(id);
    loadDelegation();
  }, [id, delegationId, loadDelegation]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleAccept = async () => {
    if (!id || !delegationId) return;
    try {
      await store.AcceptRoleDelegation(id, delegationId, { delegation: refEntity(delegationId) });
      showSuccess(t('team.delegationAccepted'));
      loadDelegation();
    } catch { /* store handles */ }
  };

  const handleReject = async () => {
    if (!id || !delegationId) return;
    try {
      await store.RejectRoleDelegation(id, delegationId, { delegation: refEntity(delegationId), reason: '' });
      showSuccess(t('team.delegationRejected'));
      loadDelegation();
    } catch { /* store handles */ }
  };

  const handleCancel = async () => {
    if (!id || !delegationId) return;
    try {
      await store.CancelRoleDelegation(id, delegationId, { delegation: refEntity(delegationId), reason: '' });
      showSuccess(t('team.delegationCancelled'));
      loadDelegation();
    } catch { /* store handles */ }
  };

  const handleExtend = async () => {
    if (!id || !delegationId) return;
    try {
      await store.ExtendRoleDelegation(id, delegationId, { delegation: refEntity(delegationId), reason: '' });
      showSuccess(t('team.delegationExtended'));
      loadDelegation();
    } catch { /* store handles */ }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !delegationId || !delegation) return;
    setSubmitting(true);
    try {
      await store.UpdateRoleDelegation(id, delegationId, {
        delegation: {
          ...delegation,
          reason_details: editForm.reason_details,
          start_at: editForm.start_at ? new Date(editForm.start_at).toISOString() : delegation.start_at,
          end_at: editForm.end_at ? new Date(editForm.end_at).toISOString() : delegation.end_at,
        },
      });
      setIsEditing(false);
      showSuccess(t('team.delegationUpdated'));
      loadDelegation();
    } catch { /* store handles */ } finally { setSubmitting(false); }
  };

  const teamName = getTeamName(store.currentItem) || t('team.title');

  if (store.isLoading && !delegation) {
    return (
      <div className="space-y-6">
        <div className="h-4 w-48 rounded bg-muted animate-pulse" />
        <div className="h-7 w-40 rounded bg-muted animate-pulse" />
        <div className="rounded-lg border border-border p-6 animate-pulse space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-4 w-full rounded bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (!delegation) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to={`/teams/${id}/delegations`} className="text-sm underline text-primary">{t('team.backToDelegations')}</Link>
      </Alert>
    );
  }

  const status = getStatusMapping(delegationStatusMap, delegation.status || '');
  const isPending = delegation.status === 'DELEGATION_STATUS_PENDING';
  const isActive = delegation.status === 'DELEGATION_STATUS_ACTIVE';
  const showCancel = canCancelDelegation(delegation.status || '');
  const reasonKey = delegationReasonMap[delegation.reason];

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground">{t('team.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground">{teamName}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}/delegations`} className="text-muted-foreground hover:text-foreground">{t('team.nav.delegations')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('team.delegationDetail')}</span>
      </nav>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {successMessage && <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('team.nav.delegations')}</h1>
        <Badge variant={status.variant as any} className="self-start sm:self-center">
          {t(status.labelKey)}
        </Badge>
        <div className="flex gap-2">
          {isPending && (
            <>
              <Button onClick={handleAccept}>{t('team.accept')}</Button>
              <Button variant="danger" onClick={handleReject}>{t('team.reject')}</Button>
            </>
          )}
          {isActive && <Button variant="secondary" onClick={handleExtend}>{t('team.extend')}</Button>}
          {(isPending || isActive) && (
            <Button variant="secondary" onClick={() => setIsEditing(!isEditing)}>
              {isEditing ? t('common.cancel') : t('team.editDelegation')}
            </Button>
          )}
          {showCancel && <Button variant="danger" onClick={handleCancel}>{t('team.cancel')}</Button>}
        </div>
      </div>

      {isEditing && (
        <Card>
          <form onSubmit={handleUpdate}>
            <CardContent className="space-y-4 pt-6">
              <Input
                label={t('team.delegationDetails')}
                value={editForm.reason_details}
                onChange={(e) => setEditForm((p) => ({ ...p, reason_details: e.target.value }))}
                placeholder={t('team.delegationDetailsPlaceholder')}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label={t('common.from')} type="datetime-local" value={editForm.start_at} onChange={(e) => setEditForm((p) => ({ ...p, start_at: e.target.value }))} />
                <Input label={t('common.to')} type="datetime-local" value={editForm.end_at} onChange={(e) => setEditForm((p) => ({ ...p, end_at: e.target.value }))} />
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setIsEditing(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={submitting}>{submitting ? t('common.saving') : t('common.saveChanges')}</Button>
            </CardFooter>
          </form>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>{t('team.delegationDetail')}</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('team.delegationField.delegator')}</dt>
              <dd className="text-sm text-foreground col-span-2">{delegation.delegator?.display_name || '--'}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('team.delegationField.delegate')}</dt>
              <dd className="text-sm text-foreground col-span-2">{delegation.delegate?.display_name || '--'}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('team.delegationField.reason')}</dt>
              <dd className="text-sm text-foreground col-span-2">
                <Badge variant="primary" size="sm">{reasonKey ? t(reasonKey) : delegation.reason}</Badge>
                {delegation.reason_details && <span className="ml-2 text-muted-foreground">{delegation.reason_details}</span>}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('team.delegationField.delegatedRoles')}</dt>
              <dd className="text-sm text-foreground col-span-2">
                {delegation.roles?.entities?.length > 0
                  ? delegation.roles.entities.map((r: any) => r.name || r.id).join(', ')
                  : t('team.allRoles')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('team.delegationField.period')}</dt>
              <dd className="text-sm text-foreground col-span-2">
                {formatDateTime(delegation.start_at, locale)} — {formatDateTime(delegation.end_at, locale)}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('team.delegationField.created')}</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDateTime(delegation.created_at, locale)}</dd>
            </div>
            {delegation.approved_by && (
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('team.delegationField.approvedBy')}</dt>
                <dd className="text-sm text-foreground col-span-2">{delegation.approved_by.display_name || '--'}</dd>
              </div>
            )}
            {delegation.cancelled_at && (
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('team.delegationField.cancelled')}</dt>
                <dd className="text-sm text-foreground col-span-2">
                  {formatDateTime(delegation.cancelled_at, locale)}
                  {delegation.cancellation_reason && <span className="ml-2 text-muted-foreground">({delegation.cancellation_reason})</span>}
                </dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Configuration */}
      <Card>
        <CardHeader><CardTitle>{t('team.delegationConfig.title')}</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 flex justify-between">
              <span className="text-sm text-muted-foreground">{t('team.delegationConfig.autoExtend')}</span>
              <Badge variant={delegation.auto_extend_if_needed ? 'success' : 'secondary'} size="sm">
                {delegation.auto_extend_if_needed ? t('common.yes') : t('common.no')}
              </Badge>
            </div>
            <div className="py-3 flex justify-between">
              <span className="text-sm text-muted-foreground">{t('team.delegationConfig.notifyOnActions')}</span>
              <Badge variant={delegation.notify_on_actions ? 'success' : 'secondary'} size="sm">
                {delegation.notify_on_actions ? t('common.yes') : t('common.no')}
              </Badge>
            </div>
            <div className="py-3 flex justify-between">
              <span className="text-sm text-muted-foreground">{t('team.delegationConfig.allowRedelegation')}</span>
              <Badge variant={delegation.allow_re_delegation ? 'success' : 'secondary'} size="sm">
                {delegation.allow_re_delegation ? t('common.yes') : t('common.no')}
              </Badge>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
});

export default TeamDelegationDetailPage;
